import React from "react";
import { motion } from "framer-motion";
import { Phone, MapPin, Clock } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { BRAND, waOrder } from "../../data/zingly";

export const Contact = () => {
  const cards = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Call Us",
      value: BRAND.phone,
      action: `tel:${BRAND.phoneRaw}`,
      tone: "bg-[#F58220]",
      testid: "contact-phone",
    },
    {
      icon: <FaWhatsapp className="w-6 h-6" />,
      title: "WhatsApp",
      value: "Chat & Order",
      action: waOrder(),
      tone: "bg-[#25D366]",
      external: true,
      testid: "contact-whatsapp",
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Location",
      value: BRAND.location,
      action: BRAND.mapsUrl,
      tone: "bg-[#2E7D32]",
      external: true,
      testid: "contact-location",
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Open Hours",
      value: BRAND.hours,
      tone: "bg-[#1E1E1E]",
      testid: "contact-hours",
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-white overflow-hidden"
    >
      <div className="absolute top-10 right-10 w-80 h-80 rounded-full bg-[#F58220]/8 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-block text-xs uppercase tracking-[0.3em] text-[#F58220] font-semibold mb-3">
            Reach Us
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#1E1E1E] heading-accent">
            Get In Touch
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#555555] max-w-2xl mx-auto">
            Order fresh homemade dosa — we&apos;re just a tap away.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map((c, i) => {
            const inner = (
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                whileHover={{ y: -10, rotateX: 5, rotateY: -3 }}
                style={{ transformStyle: "preserve-3d", perspective: "1000px" }}
                className="group h-full bg-[#FFF8F1] rounded-3xl p-7 border border-orange-100 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.1)] hover:shadow-[0_25px_50px_-12px_rgba(245,130,32,0.3)] transition-all text-center"
                data-testid={`${c.testid}-card`}
              >
                <div className={`mx-auto w-14 h-14 rounded-2xl ${c.tone} text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                  {c.icon}
                </div>
                <div className="mt-5 text-xs uppercase tracking-[0.2em] text-[#555555]">{c.title}</div>
                <div className="mt-2 font-serif text-xl font-extrabold text-[#1E1E1E]">{c.value}</div>
              </motion.div>
            );
            if (!c.action) {
              return <div key={c.title}>{inner}</div>;
            }
            return (
              <a
                key={c.title}
                href={c.action}
                target={c.external ? "_blank" : undefined}
                rel={c.external ? "noopener noreferrer" : undefined}
                data-testid={c.testid}
              >
                {inner}
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};
